import { useState } from "react";
import { createLogger } from "../../../utils/logger";
import {
  insertText,
  insertTextWithFormatting,
  insertTextWithAdvancedFormatting,
} from "../../../helpers/word";
import { getErrorMessage } from "../../../types/errors";

const log = createLogger("DocumentInsertion");

/**
 * Hook to handle document insertion operations with formatting preservation
 */
export const useDocumentInsertion = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Insert text into the document with formatting preservation
   * Falls back to simpler methods if advanced formatting fails
   */
  const insertResponseIntoDocument = async (
    text: string | null,
    preserveFormatting: boolean = true
  ): Promise<boolean> => {
    if (!text) {
      log.warn("No text provided for insertion", { preserveFormatting });
      setError("No response content available to insert");
      return false;
    }

    try {
      setBusy(true);
      log.info("Starting document insertion", {
        contentLength: text.length,
        preserveFormatting,
        contentPreview: text.substring(0, 100) + (text.length > 100 ? "..." : ""),
      });

      if (preserveFormatting) {
        try {
          log.debug("Attempting advanced formatting preservation");
          await insertTextWithAdvancedFormatting(text);
          log.info("Text inserted with advanced formatting preserved", {
            method: "advanced",
            contentLength: text.length,
          });
          return true;
        } catch (formatError) {
          log.warn("Advanced formatting failed, trying basic formatting", {
            error: getErrorMessage(formatError),
            fallbackMethod: "basic",
          });

          try {
            log.debug("Attempting basic formatting preservation");
            await insertTextWithFormatting(text);
            log.info("Text inserted with basic formatting preserved", {
              method: "basic",
              contentLength: text.length,
            });
            return true;
          } catch (basicError) {
            log.warn("Basic formatting failed, falling back to plain text", {
              error: getErrorMessage(basicError),
              fallbackMethod: "plain",
            });

            log.debug("Attempting plain text insertion as final fallback");
            await insertText(text);
            log.info("Plain text inserted successfully", {
              method: "plain",
              contentLength: text.length,
              formattingLost: true,
            });
            return true;
          }
        }
      } else {
        log.debug("Using plain text insertion by user preference");
        await insertText(text);
        log.info("Plain text inserted by user choice", {
          method: "plain",
          contentLength: text.length,
          userChoice: true,
        });
        return true;
      }
    } catch (e: any) {
      log.error("All insertion methods failed", {
        error: e.message,
        stack: e.stack,
        contentLength: text?.length || 0,
        preserveFormatting,
      });
      setError(e.message ?? "Failed to insert text");
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { insertResponseIntoDocument, busy, error, setError };
};
